import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import {
  connectWebSocket,
  sendMessage,
  markMessagesAsRead,
  sendTypingStatus,
  unsubscribe,
} from '@/api/chatService';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import createApiClient from '@/api/axiosInstance';

const ChatRoom = ({ chat, onBack, senderId, senderType }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [messagePage, setMessagePage] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const api = createApiClient(navigate, dispatch);
  const recipientType = senderType === 'BUYER' ? 'MANUFACTURER' : 'BUYER';

  // Function to load messages with pagination
  const loadMessages = async (page = 0) => {
    console.log('Loading messages for page:');
    setIsLoading(true);
    try {
      const res = await api.get(
        `/api/chats/rooms/${chat.roomId}/messages?page=${page}&size=20`,
      );

      const reversedContent = [...res.data.content].reverse();

      if (page === 0) {
        setMessages(reversedContent || []);
        scrollToBottom();
      } else {
        setMessages((prev) => [...reversedContent, ...prev]);
      }

      setHasMoreMessages(!res.data.last);

      // If this is the initial load, mark messages as read
      if (page === 0) {
        markUnreadMessages();
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark messages as read both through API and WebSocket
  const markUnreadMessages = () => {
    console.log('Marking unread messages as read');
    const unreadMessages = messages.filter(
      (msg) =>
        msg.senderId !== senderId &&
        (msg.status === 'DELIVERED' || !msg.status),
    );

    if (unreadMessages.length > 0) {
      // API call to mark messages as read
      api
        .post(`/api/chats/rooms/${chat.roomId}/read/${senderType}/${senderId}`)
        .catch((err) => console.error('Error marking messages read:', err));

      // WebSocket call for real-time status update
      markMessagesAsRead(chat.roomId, senderType, senderId);

      // Update local state for immediate feedback
      setMessages((prev) =>
        prev.map((msg) =>
          msg.senderId !== senderId &&
          (msg.status === 'DELIVERED' || !msg.status)
            ? { ...msg, status: 'READ' }
            : msg,
        ),
      );
    }
  };

  // Initial setup: load messages and set up WebSocket subscriptions
  useEffect(() => {
    console.log('Setting up chat room for:');
    loadMessages(0);

    // Subscribe to various WebSocket topics
    connectWebSocket((client) => {
      // Subscribe to new messages
      const messagesSub = client.subscribe(
        `/topic/chat/${chat.roomId}`,
        (message) => {
          const parsedMessage = JSON.parse(message.body);
          setMessages((prev) => [...prev, parsedMessage]);
          scrollToBottom();

          // If we receive a message from the other user, mark it as read immediately
          if (parsedMessage.senderId !== senderId) {
            // Short delay to ensure message is properly delivered before marking as read
            setTimeout(() => {
              markUnreadMessages();
            }, 500);
          }
        },
      );
      // Subscribe to message status updates
      const statusSub = client.subscribe(
        `/topic/chat/${chat.roomId}/status`,
        (update) => {
          const statusUpdate = JSON.parse(update.body);
          // Update messages with new status
          setMessages((prev) => {
            const updatedMessages = prev.map((msg) =>
              msg.senderId === senderId &&
              (msg.status === 'DELIVERED' || !msg.status)
                ? { ...msg, status: 'READ' }
                : msg,
            );
            return updatedMessages;
          });
        },
      );

      // Subscribe to typing status
      // const typingSub = client.subscribe(
      //   `/topic/chat/${chat.roomId}/typing`,
      //   (typing) => {
      //     const typingStatus = JSON.parse(typing.body);
      //     // Only show typing if it's from the other user
      //     if (typingStatus.userId !== senderId) {
      //       setIsTyping(typingStatus.isTyping);
      //     }
      //   },
      // );

      // Store subscription IDs for cleanup
      setActiveSubscriptions([messagesSub.id, statusSub.id]);
    });

    scrollToBottom();

    // Cleanup function for unmounting
    return () => {
      // Unsubscribe from all active subscriptions
      activeSubscriptions.forEach((subId) => unsubscribe(subId));

      // Clear typing timeout if exists
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }

      // Send stopped typing status when leaving chat
      sendTypingStatus(chat.roomId, senderId, false);
    };
  }, [chat.roomId]);

  // Auto-mark messages as read when they're viewed
  useEffect(() => {
    console.log('Checking if messages should be marked as read');
    if (!isLoading && messages.length > 0) {
      // Mark messages as read whenever they're rendered and not loading
      markUnreadMessages();
    }
  }, [isLoading, messages]);

  // Handle infinite scroll for older messages
  useEffect(() => {
    console.log('Setting up infinite scroll for messages');
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop === 0 && !isLoading && hasMoreMessages) {
        const scrollHeight = container.scrollHeight;
        loadMessages(messagePage + 1);
        setMessagePage((prev) => prev + 1);

        // Maintain scroll position after loading more messages
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - scrollHeight;
        }, 100);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messagePage, isLoading, hasMoreMessages]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    console.log('Scrolling to bottom of messages');
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Handle sending messages
  const handleSend = (content) => {
    console.log('Sending message:');
    if (!content.trim()) return;

    const msg = {
      senderId,
      senderType,
      recipientId: chat.id,
      recipientType,
      content: content.trim(),
    };

    const success = sendMessage(msg);

    if (success) {
      // Clear typing status
      sendTypingStatus(chat.roomId, senderId, false);

      // Optimistically add message to UI
      // setMessages((prev) => [
      //   ...prev,
      //   {
      //     ...msg,
      //     senderName: 'You',
      //     timestamp: new Date().toISOString(),
      //     status: 'DELIVERED',
      //   },
      // ]);

      // Scroll to bottom to show new message
      scrollToBottom();
    } else {
      // Handle failure - could show an error message or retry mechanism
      console.error('Failed to send message - WebSocket not connected');
    }
  };

  // Handle typing indicators
  const handleTyping = (isUserTyping) => {
    console.log('User is typing:');
    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Send typing indicator
    sendTypingStatus(chat.roomId, senderId, isUserTyping);

    if (isUserTyping) {
      // Set timeout to clear typing status after 2 seconds of inactivity
      const timeout = setTimeout(() => {
        sendTypingStatus(chat.roomId, senderId, false);
      }, 2000);

      setTypingTimeout(timeout);
    }
  };

  return (
    <div className="h-[calc(100vh-150px)] w-[500px] bg-white flex flex-col justify-between animate-slide-in-left mr-2">
      {/* Chat header */}
      <div className="bg-purple-200 text-gray-800 px-4 py-2 flex justify-between items-center">
        <span className="text-base font-medium">{chat.name}</span>
        <button onClick={onBack}>
          <ChevronDown className="h-6 w-6" />
        </button>
      </div>

      {/* Messages */}
      <MessageList
        messages={messages}
        isLoading={isLoading}
        messagePage={messagePage}
        isTyping={isTyping}
        chatName={chat.name}
        senderId={senderId}
        containerRef={messagesContainerRef}
        endRef={messagesEndRef}
      />

      {/* Input area */}
      <MessageInput
        handleSend={handleSend}
        onTyping={handleTyping}
        isDisabled={isLoading && messagePage === 0}
      />
    </div>
  );
};

export default ChatRoom;
